import { useState } from 'react';
import {
    Shield,
    Check,
    ChevronRight,
    User,
    Cpu
} from 'lucide-react';
import logoImage from '../assets/logo.png';

interface SetupWizardProps {
    onComplete: () => void;
}

type Step = 'welcome' | 'integrations' | 'persona' | 'review' | 'hiring';

interface Integration {
    id: string;
    name: string;
    connected: boolean;
    category: string;
}

// Define the SECURITY_TOOLS constant - organized by category
const SECURITY_TOOLS = [
    // ITSM / Ticketing - ServiceNow first!
    { id: 'servicenow', name: 'ServiceNow', category: 'ITSM' },
    { id: 'jira', name: 'Jira', category: 'Ticketing' },

    // SIEM
    { id: 'splunk', name: 'Splunk', category: 'SIEM' },
    { id: 'sentinel', name: 'Microsoft Sentinel', category: 'SIEM' },

    // EDR / XDR
    { id: 'crowdstrike', name: 'CrowdStrike', category: 'EDR' },
    { id: 'defender', name: 'Microsoft Defender', category: 'XDR' },
    { id: 'sentinelone', name: 'SentinelOne', category: 'EDR' },
    { id: 'carbonblack', name: 'Carbon Black', category: 'EDR' },

    // IAM
    { id: 'okta', name: 'Okta', category: 'IAM' },
    { id: 'azure_ad', name: 'Entra ID', category: 'IAM' },

    // Cloud
    { id: 'aws_security', name: 'AWS Security Hub', category: 'Cloud' },
    { id: 'azure_security', name: 'Azure Security', category: 'Cloud' },
    { id: 'gcp_scc', name: 'Google Cloud SCC', category: 'Cloud' },

    // Incident Response
    { id: 'pagerduty', name: 'PagerDuty', category: 'Incident' },
    { id: 'opsgenie', name: 'Opsgenie', category: 'Incident' },

    // Communication
    { id: 'slack', name: 'Slack', category: 'Communication' },
    { id: 'teams', name: 'Microsoft Teams', category: 'Communication' },

    // Vulnerability
    { id: 'tenable', name: 'Tenable', category: 'Vulnerability' },
    { id: 'qualys', name: 'Qualys', category: 'Vulnerability' },

    // Email Security
    { id: 'proofpoint', name: 'Proofpoint', category: 'Email' },
    { id: 'mimecast', name: 'Mimecast', category: 'Email' },
];

export function SetupWizard({ onComplete }: SetupWizardProps) {
    const [step, setStep] = useState<Step>('welcome');
    const [agentName, setAgentName] = useState('ZoroAgent');
    const [aggressiveness, setAggressiveness] = useState<'cautious' | 'balanced' | 'autonomous'>('cautious');

    const [integrations, setIntegrations] = useState<Integration[]>(
        SECURITY_TOOLS.map(tool => ({
            id: tool.id,
            name: tool.name,
            connected: false,
            category: tool.category,
        }))
    );

    const toggleIntegration = (id: string) => {
        setIntegrations(prev => prev.map(i =>
            i.id === id ? { ...i, connected: !i.connected } : i
        ));
    };

    const handleHiring = () => {
        setStep('hiring');
        // Simulate "hiring" process
        setTimeout(() => {
            onComplete();
        }, 3500);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl glass rounded-2xl shadow-2xl overflow-hidden animate-fade-in relative transition-all duration-500">

                {/* Progress Bar */}
                {step !== 'hiring' && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-slate-800">
                        <div
                            className="h-full bg-primary-500 transition-all duration-500 ease-out"
                            style={{
                                width: step === 'welcome' ? '25%' :
                                    step === 'integrations' ? '50%' :
                                        step === 'persona' ? '75%' : '100%'
                            }}
                        />
                    </div>
                )}

                {/* Content */}
                <div className="p-8 md:p-12 min-h-[500px] flex flex-col">

                    {/* STEP 1: WELCOME */}
                    {step === 'welcome' && (
                        <div className="flex-1 flex flex-col items-center justify-center text-center animate-slide-up">
                            <div className="w-24 h-24 mb-6 animate-float">
                                <img src={logoImage} alt="ZoroAgent Logo" className="w-full h-full object-contain filter drop-shadow-lg" />
                            </div>
                            <h1 className="text-3xl font-bold text-white mb-4">
                                Meet ZoroAgent
                            </h1>
                            <p className="text-lg text-slate-300 max-w-md mb-8">
                                Your new Autonomous Security Engineer. I can monitor threats, investigate alerts, and enforce policies 24/7.
                            </p>
                            <button
                                onClick={() => setStep('integrations')}
                                className="btn btn-primary px-8 py-3 text-lg flex items-center gap-2 group"
                            >
                                Start Onboarding <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    )}

                    {/* STEP 2: INTEGRATIONS */}
                    {step === 'integrations' && (
                        <div className="flex-1 flex flex-col animate-slide-up">
                            <div className="text-center mb-4">
                                <h2 className="text-2xl font-bold text-white mb-2">Connect Your Stack</h2>
                                <p className="text-slate-400 text-sm">Select the tools you want Zoro to manage</p>
                            </div>

                            <div className="flex-1 overflow-y-auto pr-2 space-y-4 max-h-[340px] scrollbar-thin">
                                {/* Group integrations by category */}
                                {[
                                    { label: 'ITSM & Ticketing', categories: ['ITSM', 'Ticketing'] },
                                    { label: 'SIEM & Analytics', categories: ['SIEM'] },
                                    { label: 'Endpoint Detection', categories: ['EDR', 'XDR'] },
                                    { label: 'Identity & Access', categories: ['IAM'] },
                                    { label: 'Cloud Security', categories: ['Cloud'] },
                                    { label: 'Incident & Alerting', categories: ['Incident'] },
                                    { label: 'Communication', categories: ['Communication'] },
                                    { label: 'Vulnerability & Email', categories: ['Vulnerability', 'Email'] },
                                ].map((group) => {
                                    const groupTools = integrations.filter(t => group.categories.includes(t.category));
                                    if (groupTools.length === 0) return null;
                                    return (
                                        <div key={group.label}>
                                            <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">{group.label}</div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {groupTools.map(tool => (
                                                    <button
                                                        key={tool.id}
                                                        onClick={() => toggleIntegration(tool.id)}
                                                        className={`p-2.5 rounded-lg border text-left transition-all duration-200 flex items-center gap-2 ${tool.connected
                                                            ? 'bg-primary-500/10 border-primary-500/50'
                                                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                                                            }`}
                                                    >
                                                        <span className={`font-medium text-sm ${tool.connected ? 'text-white' : 'text-slate-400'}`}>{tool.name}</span>
                                                        {tool.connected && <Check className="w-4 h-4 text-primary-500 ml-auto" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <p className="text-center text-xs text-slate-500 mt-4 mb-3">
                                Don't see your tool?{' '}
                                <a
                                    href="mailto:integrations@beitsystems.com?subject=Custom Integration Request"
                                    className="text-primary-400 hover:text-primary-300 transition-colors"
                                >
                                    Let us know →
                                </a>
                            </p>

                            <div className="flex justify-between">
                                <button onClick={() => setStep('welcome')} className="text-slate-400 hover:text-white transition-colors">Back</button>
                                <button onClick={() => setStep('persona')} className="btn btn-primary px-6">Continue</button>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: PERSONA */}
                    {step === 'persona' && (
                        <div className="flex-1 flex flex-col animate-slide-up">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-white mb-2">Agent Persona</h2>
                                <p className="text-slate-400">Configure how you want me to behave.</p>
                            </div>

                            <div className="space-y-6 max-w-md mx-auto w-full mb-8">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2">Agent Name</label>
                                    <input
                                        type="text"
                                        value={agentName}
                                        onChange={(e) => setAgentName(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-4">Autonomy Level</label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { id: 'cautious', label: 'Cautious', desc: 'Ask for everything' },
                                            { id: 'balanced', label: 'Balanced', desc: 'Standard Ops' },
                                            { id: 'autonomous', label: 'Autonomous', desc: 'Act fast' },
                                        ].map((level) => (
                                            <button
                                                key={level.id}
                                                onClick={() => setAggressiveness(level.id as any)}
                                                className={`p-3 rounded-lg border text-center transition-all ${aggressiveness === level.id
                                                    ? 'bg-primary-500/20 border-primary-500 text-white shadow-[0_0_15px_rgba(var(--primary-500),0.3)]'
                                                    : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                                                    }`}
                                            >
                                                <div className="font-medium text-sm mb-1">{level.label}</div>
                                                <div className="text-[10px] opacity-70">{level.desc}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto flex justify-between">
                                <button onClick={() => setStep('integrations')} className="text-slate-400 hover:text-white transition-colors">Back</button>
                                <button onClick={() => setStep('review')} className="btn btn-primary px-6">Continue</button>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: REVIEW */}
                    {step === 'review' && (
                        <div className="flex-1 flex flex-col animate-slide-up">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-white mb-2">Review & Hire</h2>
                                <p className="text-slate-400">Ready to bring {agentName} on board?</p>
                            </div>

                            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
                                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-700/50">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-primary-500/20 rounded-lg text-primary-400">
                                            <User className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-slate-400">Identity</div>
                                            <div className="font-medium text-white">{agentName}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-slate-400">Role</div>
                                        <div className="font-medium text-white">Security Engineer</div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-700/50">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                            <Cpu className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-slate-400">Mode</div>
                                            <div className="font-medium text-white capitalize">{aggressiveness}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-slate-400">Model</div>
                                        <div className="font-medium text-white">Gemini 1.5 Flash</div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <div className="text-sm text-slate-400">Access Granted</div>
                                    <div className="flex flex-wrap gap-2">
                                        {integrations.filter(i => i.connected).map(i => (
                                            <span key={i.id} className="px-2 py-1 bg-slate-700 rounded text-xs text-white border border-slate-600 flex items-center gap-1">
                                                {i.name} <Check className="w-3 h-3 text-green-400" />
                                            </span>
                                        ))}
                                        {integrations.every(i => !i.connected) && (
                                            <span className="text-xs text-slate-500 italic">No tools connected (Demo Mode)</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto flex justify-between">
                                <button onClick={() => setStep('persona')} className="text-slate-400 hover:text-white transition-colors">Back</button>
                                <button
                                    onClick={handleHiring}
                                    className="btn btn-success px-8 py-3 text-lg w-full ml-4 shadow-[0_0_20px_rgba(var(--success-500),0.3)] animate-pulse-slow"
                                >
                                    Hire {agentName}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* STEP 5: HIRING ANIMATION */}
                    {step === 'hiring' && (
                        <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in">
                            <div className="relative mb-8">
                                <div className="absolute inset-0 bg-primary-500/30 blur-3xl rounded-full animate-pulse-slow"></div>
                                <Shield className="w-24 h-24 text-primary-500 relative z-10 animate-scale-in" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-primary-500/30 rounded-full animate-ping-slow"></div>
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2">Initializing Agent...</h2>
                            <div className="text-slate-400 flex flex-col gap-2 h-20 overflow-hidden">
                                <span className="animate-slide-up-fade [animation-delay:0.5s]">Loading memory core...</span>
                                <span className="animate-slide-up-fade [animation-delay:1.0s]">Connecting to {integrations.filter(i => i.connected).length} integrations...</span>
                                <span className="animate-slide-up-fade [animation-delay:1.5s]">Verifying security policies...</span>
                                <span className="animate-slide-up-fade [animation-delay:2.0s] text-green-400 font-medium">System Online. Welcome aboard.</span>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
